if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });

        document.dispatchEvent(new Event('freeGiftsUpdate'));
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // console.log('Product info updated successfully', targetId);
            // set focus to last clicked option value
            // document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          if (document.querySelector('.subscription_box') && html.querySelector('.subscription_box')) {
            setTimeout(() => {
              const stuckLabels = document.querySelectorAll('.stuck_bundle_item > label');
              if (!stuckLabels.length) return;

              const isOneTime =
                document.querySelector('.subs-box-options label.option-card > input:checked')?.value === 'oneime_op';
              const attr = isOneTime ? 'data-onetime-per-wash' : 'data-subs-per-wash';

              stuckLabels.forEach((stuck) => {
                const perWashEl = stuck.querySelector('#per_wash');
                if (perWashEl) perWashEl.innerHTML = stuck.getAttribute(attr);

                const stuckValue = stuck.getAttribute('data-stuck');

                if (stuck.previousElementSibling?.checked && stuckValue) {
                  const freqInputs = document.querySelectorAll('.subs-frequency .freq-options label > input');

                  freqInputs.forEach((input) => {
                    const monthValue = input.getAttribute('data-month');

                    if (monthValue === stuckValue) {
                      input.checked = true;
                      input.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                  });
                }
              });
            }, 0);
          }

          if (document.querySelector('.subscription_box') && html.querySelector('.subscription_box')) {
            //document.querySelector('.subscription_box').innerHTML = html.querySelector('.subscription_box').innerHTML;

            if (document.querySelector('.sub-badge')) {
              document.querySelector('.sub-badge').innerHTML = html.querySelector('.sub-badge').innerHTML;
            }
            if (document.querySelector('.toggle-box-header .sub-percent span')) {
              document.querySelector('.toggle-box-header .sub-percent span').innerHTML = html.querySelector(
                '.toggle-box-header .sub-percent span'
              ).innerHTML;
            }
            if (document.querySelector('.subs-pricing')) {
              document.querySelector('.subs-pricing').innerHTML = html.querySelector('.subs-pricing').innerHTML;
            }
            if (document.querySelector('.onetime-pricing')) {
              document.querySelector('.onetime-pricing').innerHTML = html.querySelector('.onetime-pricing').innerHTML;
            }

            //document.querySelector('.subs-frequency .freq-options').innerHTML = html.querySelector('.subs-frequency .freq-options').innerHTML;
            const sourceInputs = html.querySelectorAll('.subs-frequency .freq-options label > input');

            // get all inputs from live DOM
            const targetInputs = document.querySelectorAll('.subs-frequency .freq-options label > input');

            // loop through and copy IDs
            sourceInputs.forEach((el, index) => {
              const monthNumber = el.getAttribute('data-month-number');
              const id = el.getAttribute('data-selling-id');
              const month = el.getAttribute('data-month');
              if (targetInputs[index]) {
                targetInputs[index].setAttribute('data-selling-id', id);

                if (month) {
                  targetInputs[index].setAttribute('data-month', month);
                }

                const span = targetInputs[index].closest('label').querySelector('span');
                if (span && monthNumber) {
                  span.textContent = monthNumber;
                }
              }
            });

            setTimeout(() => {
              let sellingPlanInput = document.querySelector('.bundle_product_form input[name="selling_plan"]');
              let checkedOption = document.querySelector('.subs-box-options label.option-card > input:checked');

              if (checkedOption.value == 'subscription_op') {
                if (document.querySelector('.ad-bundles')) {
                  document.querySelector('.ad-bundles').classList.remove('onetime_activate');
                  document.querySelector('.ad-bundles').classList.add('subscription_activate');
                }

                if (document.querySelector('.subscription_pricing')) {
                  document.querySelector('.subscription_pricing').style.display = 'flex';
                }

                if (document.querySelector('.one_time_pricing')) {
                  document.querySelector('.one_time_pricing').style.display = 'none';
                }

                let selectedEl = document.querySelector('.subs-frequency .freq-options input:checked');
                let selectedFreq = selectedEl?.getAttribute('data-selling-id');

                if (sellingPlanInput && selectedFreq) {
                  sellingPlanInput.value = selectedFreq;
                }
              } else {
                if (document.querySelector('.ad-bundles')) {
                  document.querySelector('.ad-bundles').classList.remove('subscription_activate');
                  document.querySelector('.ad-bundles').classList.add('onetime_activate');
                }

                if (document.querySelector('.subscription_pricing')) {
                  document.querySelector('.subscription_pricing').style.display = 'none';
                }
                if (document.querySelector('.one_time_pricing')) {
                  document.querySelector('.one_time_pricing').style.display = 'flex';
                }

                if (sellingPlanInput) {
                  sellingPlanInput.value = '';
                }
              }
            }, 0);

            if (document.querySelector('.bundle_product_form input[name="selling_plan"]')) {
              document.querySelector('.bundle_product_form input[name="selling_plan"]').value = html.querySelector(
                '.bundle_product_form input[name="selling_plan"]'
              ).value;
            }
          }

          if (typeof window.customOnVariantChange === 'function') {
            window.customOnVariantChange(variant);
          }
          if (typeof window.mpcBundle == 'function') {
            window.mpcBundle(variant);
          }

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        const params = new URLSearchParams(window.location.search);
        if (variantId) params.set('variant', variantId); else params.delete('variant');
        const qs = params.toString();
        window.history.replaceState({}, '', `${url}${qs ? `?${qs}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const gallery = this.querySelector('media-gallery');
        if (!gallery || !gallery.viewerSwiper || !gallery.thumbsSwiper) return;

        const viewerSwiper = gallery.viewerSwiper;
        const thumbsSwiper = gallery.thumbsSwiper;

        const newViewerWrapper = html.querySelector('media-gallery [id^="GalleryViewer"] .swiper-wrapper');

        const newThumbsWrapper = html.querySelector('media-gallery [id^="GalleryThumbnails"] .swiper-wrapper');

        if (!newViewerWrapper || !newThumbsWrapper) return;

        // Enable zoom again if needed
        if (this.hasAttribute('data-zoom-on-hover')) {
          enableZoomOnHover(2);
        }

        // ---- VIEWER ----
        const newViewerSlides = Array.from(newViewerWrapper.children).map((slide) => slide.outerHTML);

        viewerSwiper.removeAllSlides();
        viewerSwiper.appendSlide(newViewerSlides);
        viewerSwiper.update();

        // ---- THUMBNAILS ----
        const newThumbSlides = Array.from(newThumbsWrapper.children).map((slide) => slide.outerHTML);

        thumbsSwiper.removeAllSlides();
        thumbsSwiper.appendSlide(newThumbSlides);
        thumbsSwiper.update();

        // ---- Sync both swipers ----
        viewerSwiper.update();
        thumbsSwiper.update();

        // ---- Set active media ----
        const targetMediaId = `${this.dataset.section}-${variantFeaturedMediaId}`;

        const slides = Array.from(viewerSwiper.slides);
        const index = slides.findIndex((slide) => slide.dataset.mediaId === targetMediaId);

        if (index !== -1) {
          viewerSwiper.slideTo(index, 0);
          thumbsSwiper.slideTo(index, 0);
        }

        // ---- Trigger existing gallery logic ----
        gallery.setActiveMedia?.(targetMediaId, true);

        // ---- Update modal ----
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);

        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);

        if (modalContent && newModalContent) {
          modalContent.innerHTML = newModalContent.innerHTML;
        }
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
